import { AutoReviewSavedEvidence, FixGuidanceCard } from '@overlayqa/shared/ui';
import { resolveAssetUrl } from '@/lib/api/client';
import { useState, useRef, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useIssue, useUpdateIssue, useDeleteIssue } from '../hooks/useTickets';
import { useNotionAutoSync } from '../hooks/useNotionAutoSync';
import { useProject } from '../hooks/useProjects';
import { useTeam } from '../hooks/useTeams';
import { CommentThread } from '../components/comments/CommentThread';
import { DeletionConfirmDialog } from '@/components/shared/DeletionConfirmDialog';
import type { IssueSeverity, IssueStatus, IssueType } from '@overlayqa/shared';

import {
  IssueBreadcrumb,
  IssueTitleCard,
  IssueDetailsCard,
  IssueActions,
  EnvironmentSection,
  ElementDataSection,
  ExportedToSection,
  FigmaEmbedPanel,
} from './ticket-detail';
import { IssueDetailSkeleton, IssueDetailError } from './ticket-detail/IssueDetailStates';
import { ScreenshotCard } from './ticket-detail/ScreenshotCard';
import { IssueVerificationDetails } from './ticket-detail/IssueVerificationDetails';
import { buildIssueEnvironment } from './ticket-detail/issueEnvironment';
import { useIssueVerification } from './ticket-detail/useIssueVerification';
import { MoveIssuesDialog } from '../components/issues/MoveIssuesDialog';
import { useMoveIssue } from '../hooks/useBulkActions';
import { useProjects } from '../hooks/useProjects';
import { log } from '@/lib/logger';
import { capture } from '@/lib/analytics';
import { canWriteProject } from '@/lib/projectPermissions';

export function IssueDetailPage() {
  const { ticketId } = useParams<{ ticketId: string }>();
  const navigate = useNavigate();
  const { data: ticket, isLoading, isFetching, error, refetch } = useIssue(ticketId || '');
  const updateIssueMutation = useUpdateIssue();
  const deleteIssueMutation = useDeleteIssue();

  // Auto-sync Notion status if this issue was exported to Notion
  useNotionAutoSync(ticket);

  log.info('[IssueDetailPage] Render', { ticketId, isLoading });

  // Verification state + handlers (load effect, verify handoff, mark/reopen/skip).

  // Delete confirmation
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);

  // Move to another project. The picker lives here rather than inside
  // IssueActions because the page owns what happens AFTER the move: this URL
  // can name the issue by displayId (/issues/OQ-127) and the server re-mints
  // that handle, so the page has to follow itself to the new one.
  const [moveOpen, setMoveOpen] = useState(false);
  const moveIssue = useMoveIssue();

  // Figma state
  const hasFigmaLink = Boolean(ticket?.figmaFileKey);

  // Resolve project's team to fetch members for @mention autocomplete
  const { data: project } = useProject(ticket?.projectId ?? '');
  const canWrite = canWriteProject(project?.role) && !project?.archivedAt;
  const verification = useIssueVerification(ticket, updateIssueMutation, !canWrite);
  const canWriteRef = useRef(canWrite);
  canWriteRef.current = canWrite;
  useEffect(() => {
    if (!canWrite) { setConfirmDeleteOpen(false); setMoveOpen(false); }
  }, [canWrite]);
  // Move destinations: the issue's own team, which is the only scope the
  // server accepts (it refuses a cross-team destination outright).
  const { data: teamProjects } = useProjects(project?.teamId);

  const demoIssueTracked = useRef(false);
  useEffect(() => {
    if (project?.isDemo && ticketId && !demoIssueTracked.current) {
      demoIssueTracked.current = true;
      capture('demo_issue_viewed', { ticketId, projectId: ticket?.projectId });
    }
  }, [project?.isDemo, ticketId, ticket?.projectId]);

  const projectTeamId = project?.teamId;
  const { data: teamData, isLoading: membersLoading, error: membersError, refetch: refetchMembers } = useTeam(projectTeamId ?? '');

  // --- Event handlers ---

  const handleDelete = () => {
    if (!ticketId || !canWriteRef.current) return;
    setConfirmDeleteOpen(true);
  };

  const confirmDelete = async () => {
    if (!ticketId || !canWriteRef.current) return;
    try {
      await deleteIssueMutation.mutateAsync(ticketId);
      setConfirmDeleteOpen(false);
      navigate(-1);
    } catch (err) {
      log.error('Failed to delete issue:', { err });
      toast.error(err instanceof Error && err.message ? err.message : 'Failed to delete issue. Please try again.');
    }
  };

  const handleSaveFields = async (data: { type?: IssueType; severity?: IssueSeverity; status?: IssueStatus; assigneeId?: string | null }, via?: 'not_detected_chip') => {
    if (!ticketId || !canWriteRef.current) return;
    log.info('[IssueDetailPage] Saving field changes:', { data });
    try {
      await updateIssueMutation.mutateAsync({
        id: ticketId,
        data: via ? { ...data, via } : data,
      });
      log.info('[IssueDetailPage] Field changes saved successfully');
      // issue_status_changed is announced by the server PATCH handler for every
      // surface (services/issueStatusAnalytics.ts), never here.
    } catch (err) {
      log.error('[IssueDetailPage] Failed to save field changes:', { err });
      throw err;
    }
  };

  // --- Loading / error states ---

  if (isLoading) {
    return <IssueDetailSkeleton />;
  }

  if (error || !ticket) {
    return <IssueDetailError error={error ?? new Error('This issue is unavailable.')} onRetry={() => void refetch()} retrying={isFetching} />;
  }

  // --- Derived state ---

  const showFigma = hasFigmaLink;

  const { environment, hasEnvironmentData, teamMembers, createdByName } = buildIssueEnvironment(ticket, teamData);
  log.info('[IssueDetailPage] Environment data:', { environment });

  // --- Render ---

  return (
    <section id="issue-detail" className="page-content">
      {/* Breadcrumb + Actions */}
      <div className="flex items-center justify-between mb-4">
        <IssueBreadcrumb ticket={ticket} ticketId={ticketId!} />
        {/* ticket.id, never the route param: issue links use displayId URLs
            (/issues/US-2) and the export routes are UUID-only. */}
        {canWrite && <IssueActions
          issueId={ticket.id}
          onDelete={handleDelete}
          isDeleting={deleteIssueMutation.isPending}
          onMoveClick={() => setMoveOpen(true)}
        />}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Main Content Column */}
        <div className="lg:col-span-2 space-y-6">
          {/* Title + badges + description */}
          <IssueTitleCard ticket={ticket} members={teamMembers}
            onSaveDescription={canWrite
              ? description => {
                if (!canWriteRef.current) return Promise.reject(new Error('You have view-only access to this project.'));
                return updateIssueMutation.mutateAsync({ id: ticket.id, data: { description } });
              }
              : undefined} />

          {/* Screenshot + fullscreen lightbox */}
          <ScreenshotCard screenshotUrl={ticket.screenshotUrl} fullPageScreenshotUrl={ticket.fullPageScreenshotUrl} comparisonContext={ticket.elementData?.comparisonContext} clientReviewVersion={ticket.clientReviewVersion} />

          {/* Verification card — only when the issue is in a verifiable
              state. Keep verification alongside the captured evidence,
              after the issue explanation. */}
          {verification.showVerification && <IssueVerificationDetails readOnly={!canWrite} ticket={ticket} verification={verification} />}

          {/* DOM element data + design tokens (if captured) */}
          <AutoReviewSavedEvidence appearance="dashboard" evidence={ticket.elementData?.autoReview} resolveImageUrl={resolveAssetUrl} />
          {ticket.fixGuidance && <div className="oq-auto-review-ui"><FixGuidanceCard fix={ticket.fixGuidance} /></div>}
          <ElementDataSection ticket={ticket} />
        </div>

        {/* Figma Panel + Details + Environment + Comments (when Figma shown) */}
        {showFigma && (
          <div className="space-y-6">
            <FigmaEmbedPanel ticket={ticket} ticketId={ticketId!} />
            <IssueDetailsCard ticket={ticket} compact createdByName={createdByName} teamMembers={teamMembers} membersLoading={membersLoading || !project} membersError={!!membersError} onRetryMembers={() => { void refetchMembers(); }} onSaveFields={canWrite ? handleSaveFields : undefined} isSaving={updateIssueMutation.isPending} />
            {hasEnvironmentData && (
              <EnvironmentSection environment={environment} />
            )}
            <ExportedToSection ticket={ticket} />
            <CommentThread key={ticketId} issueId={ticketId!} teamMembers={teamMembers} />
          </div>
        )}

        {/* Sidebar (when no Figma link) */}
        {!showFigma && (
          <aside className="space-y-6">
            <IssueDetailsCard ticket={ticket} createdByName={createdByName} teamMembers={teamMembers} membersLoading={membersLoading || !project} membersError={!!membersError} onRetryMembers={() => { void refetchMembers(); }} onSaveFields={canWrite ? handleSaveFields : undefined} isSaving={updateIssueMutation.isPending} />
            {hasEnvironmentData && (
              <EnvironmentSection environment={environment} />
            )}
            <ExportedToSection ticket={ticket} />
            <CommentThread key={ticketId} issueId={ticketId!} teamMembers={teamMembers} />
          </aside>
        )}
      </div>

      <DeletionConfirmDialog
        open={canWrite && confirmDeleteOpen}
        request={{ kind: 'issues', ids: [ticket.id] }}
        fallback={{ title: 'Delete issue', message: 'Are you sure you want to delete this issue? This cannot be undone.', confirmLabel: 'Delete' }}
        onConfirm={confirmDelete}
        onCancel={() => setConfirmDeleteOpen(false)}
        loading={deleteIssueMutation.isPending}
      />

      <MoveIssuesDialog
        open={canWrite && moveOpen}
        issueIds={[ticket.id]}
        currentProjectId={ticket.projectId}
        projects={teamProjects ?? []}
        moving={moveIssue.isPending}
        onCancel={() => setMoveOpen(false)}
        onMove={async (targetProjectId, targetProjectName) => {
          if (!canWriteRef.current) return;
          setMoveOpen(false);
          try {
            const result = await moveIssue.mutateAsync({
              issueId: ticket.id,
              targetProjectId,
              targetProjectName,
            });
            // The route param may be the OLD displayId, which the server no
            // longer resolves. Replace it with the re-minted handle so a
            // refresh or a back-forward does not land on a 404.
            const nextHandle = result.data.displayId ?? result.data.id;
            if (nextHandle && nextHandle !== ticketId) {
              navigate(`/issues/${nextHandle}`, { replace: true });
            }
          } catch {
            // useMoveIssue already toasted the server's reason.
          }
        }}
      />
    </section>
  );
}

/** @deprecated Use IssueDetailPage */
export const TicketDetailPage = IssueDetailPage;
